import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { PermissionGroupAssignedUserDetailsParams } from 'src/apis/authorization-service/types';
import type { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import { useOrganisationAssignedLegacyPermissions } from 'src/apis/authorization-service/use-legacy-permissions';
import { useOrganisationSettings } from 'src/apis/authorization-service/use-organisation-settings';
import { usePermissionGroup } from 'src/apis/authorization-service/use-permission-group';
import { usePermissionGroupAssignableUsers } from 'src/apis/authorization-service/use-permission-group-assignable-users';
import { usePermissionGroupAssignedUsers } from 'src/apis/authorization-service/use-permission-group-assigned-users';
import { usePermissionGroups } from 'src/apis/authorization-service/use-permission-groups';
import { usePermissions } from 'src/apis/authorization-service/use-permissions';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { usePageParams } from 'src/pages/settings/permission-groups/details/use-page-params';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(() => import('./page'));

type Props = {
  authorizationService: Successful<AuthorizationServiceRequest>;
};

export const PermissionGroupsDetails: FC<Props> = ({
  authorizationService,
}) => {
  const { permissionGroupId } = useParams<{ permissionGroupId: string }>();
  const permissionGroups = usePermissionGroups({
    authorizationService,
  });
  const permissionGroup = usePermissionGroup({
    permissionGroups,
    permissionGroupId,
  });
  const organisationLegacyPermissions =
    useOrganisationAssignedLegacyPermissions({
      authorizationService,
      permissionGroup,
    });

  const pageParams = usePageParams();

  const assignedUsersParams = useMemo(
    (): PermissionGroupAssignedUserDetailsParams => ({
      limit: pageParams.value.pageSize,
      offset: pageParams.value.start,
    }),
    [pageParams.value]
  );

  const assignedUsers = usePermissionGroupAssignedUsers({
    assignedUsersLink: permissionGroup?.response?.data._links.assignedUsers,
    params: assignedUsersParams,
  });
  const assignableUsers = usePermissionGroupAssignableUsers({
    assignableUsersLink: permissionGroup?.response?.data._links.assignableUsers,
  });

  const permissions = usePermissions({ authorizationService });
  const organisationSettings = useOrganisationSettings({
    authorizationService,
  });

  if (!permissions) throw new Forbidden();
  if (!permissionGroup) throw new Forbidden();
  if (!permissionGroups) throw new Forbidden();

  if (
    !permissions.response ||
    !permissionGroup.response ||
    !organisationLegacyPermissions?.response ||
    assignedUsers?.response === null ||
    assignableUsers?.response === null ||
    !organisationSettings?.response
  )
    return <PageSpinner />;

  return (
    <Page
      createLink={permissionGroups.response?.data._links.create}
      permissions={permissions}
      permissionGroup={permissionGroup}
      assignedUsers={assignedUsers}
      assignableUsers={assignableUsers}
      organisationLegacyPermissions={organisationLegacyPermissions}
      isAdvancedModeEnabled={
        organisationSettings.response.data.isAdvanceModeEnabled
      }
    />
  );
};
